import api from 'api'
import { CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST } from 'api/cacheTagTypes'
import { withIdTag } from 'utils/cache'

/**
 * Builds FormData for invoice email API
 * @param {Object} params
 * @param {string[]} params.invoiceIds - Array of invoice UUIDs
 * @param {Object} params.customEmailNotification - Email settings
 * @param {string} params.customEmailNotification.toRecipientEmails - Comma-separated to recipients
 * @param {string} params.customEmailNotification.ccRecipientEmails - Comma-separated CC recipients
 * @param {string} params.customEmailNotification.subject - Email subject
 * @param {string} params.customEmailNotification.body - Email body (HTML)
 * @param {boolean} params.customEmailNotification.attachDefaultInvoice - Whether to attach default invoice PDF
 * @param {File[]|null} params.customEmailNotification.attachments - File attachments or null
 * @returns {FormData}
 */
const buildInvoiceEmailFormData = ({ invoiceIds, customEmailNotification }) => {
  const formData = new FormData()

  // Append each invoice ID
  invoiceIds.forEach(id => {
    formData.append('invoiceIds', id)
  })

  // Append email settings as individual form fields
  formData.append('customEmailNotification.toRecipientEmails', customEmailNotification.toRecipientEmails)
  formData.append('customEmailNotification.ccRecipientEmails', customEmailNotification.ccRecipientEmails || '')
  formData.append('customEmailNotification.subject', customEmailNotification.subject)
  formData.append('customEmailNotification.body', customEmailNotification.body)
  formData.append('customEmailNotification.attachDefaultInvoice', String(customEmailNotification.attachDefaultInvoice))

  // Append file attachments
  const { attachments } = customEmailNotification
  if (attachments?.length > 0) {
    attachments.forEach(file => {
      formData.append('customEmailNotification.attachments', file)
    })
  }

  return formData
}

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    sendInvoiceList: build.mutation({
      query: ({ invoiceIds }) => ({
        url: '/api/v1/billing/invoice/send',
        method: 'POST',
        body: { invoiceIds },
      }),
      invalidatesTags: (result, error, arg) => withIdTag(CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST, arg?.accountId),
    }),
    sendCustomEmailInvoiceList: build.mutation({
      query: ({ invoiceIds, customEmailNotification }) => ({
        url: '/api/v1/billing/invoice/send',
        method: 'POST',
        body: buildInvoiceEmailFormData({ invoiceIds, customEmailNotification }),
      }),
      invalidatesTags: (result, error, arg) => withIdTag(CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST, arg?.accountId),
    }),
    bulkSendInvoiceList: build.mutation({
      query: body => ({ url: '/api/v1/billing/invoice/bulk/send', method: 'POST', body }),
      invalidatesTags: [CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST],
    }),
    countEmailRecipients: build.mutation({
      query: ({ invoiceIds }) => ({
        url: '/api/v1/billing/invoice/email-recipient-count',
        method: 'POST',
        body: { invoiceIds },
      }),
    }),
  }),
})

export const {
  useSendCustomEmailInvoiceListMutation,
  useBulkSendInvoiceListMutation,
  useSendInvoiceListMutation,
  useCountEmailRecipientsMutation,
} = extendedApi
